/**
 * Parallax Text Scroller
 * Horizontal parallax effect on scroll
 *
 * Features:
 * - Progressive Enhancement (works without JS)
 * - Performance-optimized with RAF and Intersection Observer
 * - Respects prefers-reduced-motion
 * - GPU-accelerated animations
 * - iOS Safari optimized (cached element position)
 */

if (!customElements.get('parallax-text-scroller')) {
  class ParallaxTextScroller extends HTMLElement {
    constructor() {
      super();

      // Settings from data attributes
      this.speed = parseFloat(this.dataset.speed) || 0.3;
      this.direction = this.dataset.direction || 'left';
      this.parallaxElement = this.querySelector('[data-parallax-element]');

      // Animation state
      this.isVisible = false;
      this.ticking = false;
      this.currentScroll = 0;

      // Check if reduced motion is preferred (Accessibility)
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      // Graceful degradation: Don't initialize if reduced motion or no element
      if (this.prefersReducedMotion || !this.parallaxElement) {
        return;
      }

      this.init();
    }

    init() {
      // Mark as initialized (CSS uses this for styling)
      this.classList.add('parallax-initialized');

      // Cache element position for performance (iOS optimization)
      this.cacheElementPosition();

      // Update cache on resize
      this.handleResizeBound = this.cacheElementPosition.bind(this);
      window.addEventListener('resize', this.handleResizeBound);

      // Setup Intersection Observer for performance
      // Only animate when section is visible in viewport
      this.setupIntersectionObserver();

      // Scroll event with RequestAnimationFrame
      this.boundUpdate = this.update.bind(this);
      this.handleScrollBound = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.handleScrollBound, { passive: true });

      // Initial update
      this.update();
    }

    cacheElementPosition() {
      // Cache getBoundingClientRect() result for performance
      // Calling getBoundingClientRect() every frame is expensive, especially on iOS
      const rect = this.getBoundingClientRect();
      this.elementTop = rect.top + window.pageYOffset;
    }

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '100px 0px', // Start animation slightly before visible
        threshold: 0
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.update();
          }
        });
      }, options);

      this.observer.observe(this);
    }

    handleScroll() {
      // Use RequestAnimationFrame to avoid scroll event spam
      if (!this.ticking && this.isVisible) {
        window.requestAnimationFrame(this.boundUpdate);
        this.ticking = true;
      }
    }

    update() {
      if (!this.isVisible) {
        this.ticking = false;
        return;
      }

      // Calculate scroll position
      const scrollY = window.pageYOffset || window.scrollY;

      // Use cached element position (updated on resize)
      // iOS Safari: getBoundingClientRect() is expensive and inconsistent during scroll
      const elementTop = this.elementTop;

      // Calculate offset based on scroll position and element position
      let offset = (scrollY - elementTop + window.innerHeight / 2) * this.speed;

      // Reverse direction if set to "right"
      if (this.direction === 'right') {
        offset = -offset;
      }

      // Apply transform with GPU acceleration (translate3d)
      this.parallaxElement.style.transform = `translate3d(${offset}px, 0px, 0px)`;

      this.ticking = false;
    }

    disconnectedCallback() {
      // Cleanup: Remove event listeners and observers
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.handleScrollBound) {
        window.removeEventListener('scroll', this.handleScrollBound);
      }
      if (this.handleResizeBound) {
        window.removeEventListener('resize', this.handleResizeBound);
      }
    }
  }

  customElements.define('parallax-text-scroller', ParallaxTextScroller);
}
